import React from 'react';
import { Sparkles, GitBranch, ShieldCheck, Leaf, Users, ChartNoAxesCombined, PanelsTopLeft, BookOpen, Blocks } from 'lucide-react';
import './BrandCover.css';

const themes = {
  'AI Tools': [Sparkles, '#873b83', '#f1e7f0'],
  'Developer Tools': [GitBranch, '#087e8b', '#e0f0ef'],
  'Cybersecurity': [ShieldCheck, '#087e8b', '#e0f0ef'],
  'AI & Compliance': [ShieldCheck, '#a65728', '#f7ebdf'],
  'Microsoft 365': [PanelsTopLeft, '#087e8b', '#e0f0ef'],
  'ESG': [Leaf, '#397459', '#e5eee6'],
  'B-BBEE': [Users, '#087e8b', '#e0f0ef'],
  'Employment Equity': [Users, '#873b83', '#f1e7f0'],
  'Skills Development': [BookOpen, '#a65728', '#f7ebdf'],
  'Finance': [ChartNoAxesCombined, '#873b83', '#f1e7f0'],
  'Claude Skill': [Blocks, '#873b83', '#f1e7f0'],
};

// Course-specific cover icons, chosen with `coverIcon` on the course.
function CreditDocumentIcon({ size = 36, strokeWidth = 1.4 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 21H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8l4 4v4" />
      <path d="M9.5 6.5c-.3-.6-1-1-1.8-1-1 0-1.7.6-1.7 1.4 0 1.9 3.5 1 3.5 2.9 0 .8-.8 1.4-1.8 1.4-.8 0-1.5-.4-1.8-1M7.75 4.5v1m0 5.7v1" />
      <path d="M7 15h5M7 18h4" />
      <path d="M18.5 12.5l3.5 1.4v2.4c0 2.3-1.5 4-3.5 4.7-2-.7-3.5-2.4-3.5-4.7v-2.4z" />
      <path d="M17.1 16.6l1 1 1.8-2" />
    </svg>
  );
}
const COVER_ICONS = { credit: CreditDocumentIcon };

// mulberry32, seeded from the title, gives each cover its own reproducible
// stream of numbers so the art below stays the same on every render but
// varies continuously (not just across 6 buckets) from one title to the next.
function mulberry32(seed) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildArt(seedText) {
  const seed = Array.from(seedText).reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 0);
  const rand = mulberry32(seed);
  const r = Array.from({ length: 8 }, () => rand());
  const family = Math.floor(r[0] * 6);

  if (family === 0) {
    const count = 3 + Math.floor(r[1] * 3);
    const step = 14 + r[2] * 16;
    const strokeWidth = 5 + r[3] * 7;
    const cx = 138 + r[4] * 34;
    return <g stroke="currentColor" strokeWidth={strokeWidth}>{Array.from({ length: count }, (_, i) => <circle key={i} cx={cx} cy="95" r={20 + i * step} />)}</g>;
  }
  if (family === 1) {
    const count = 3 + Math.floor(r[1] * 3);
    const strokeWidth = 3 + r[2] * 4;
    const amp = 18 + r[3] * 34;
    const spacing = 12 + r[4] * 15;
    const phase = 10 + r[5] * 40;
    return <g stroke="currentColor" strokeWidth={strokeWidth}>{Array.from({ length: count }, (_, i) => {
      const y = 20 + i * spacing;
      return <path key={i} d={`M12 ${y + amp * 0.3} C${65 + phase} ${y - amp} ${105 - phase} ${y + amp} 155 ${y} S${215 + phase} ${y - amp * 0.6} 245 ${y + amp * 0.2}`} />;
    })}</g>;
  }
  if (family === 2) {
    const count = 3 + Math.floor(r[1] * 3);
    const startX = 26 + r[2] * 24;
    const gapX = 32 + r[3] * 22;
    const width = 20 + r[4] * 18;
    const baseHeight = 16 + r[5] * 18;
    const stepHeight = 10 + r[6] * 16;
    return <g fill="currentColor">{Array.from({ length: count }, (_, i) => {
      const h = baseHeight + i * stepHeight;
      return <rect key={i} x={startX + i * gapX} y={95 - h} width={width} height={h} rx={width / 2} />;
    })}</g>;
  }
  if (family === 3) {
    const count = 3 + Math.floor(r[1] * 3);
    const size = 52 + r[2] * 32;
    const stepX = 15 + r[3] * 17;
    const stepY = 4 + r[4] * 12;
    const rotation = -32 + r[5] * 44;
    const strokeWidth = 3 + r[6] * 3;
    return <g stroke="currentColor" strokeWidth={strokeWidth}>{Array.from({ length: count }, (_, i) => {
      const x = 52 + i * stepX, y = 8 + i * stepY;
      return <rect key={i} x={x} y={y} width={size} height={size} rx="18" transform={`rotate(${rotation} ${x + size / 2} ${y + size / 2})`} />;
    })}</g>;
  }
  if (family === 4) {
    const arcR = 55 + r[1] * 42;
    const arcCx = 35 + r[2] * 30;
    const dotR = 10 + r[3] * 15;
    const dotX = 168 + r[4] * 48;
    const dotY = 10 + r[5] * 30;
    return <g fill="currentColor"><path d={`M${arcCx - arcR * 0.55} 92A${arcR} ${arcR} 0 0 1 ${arcCx + arcR * 1.2} 92Z`} /><circle cx={dotX} cy={dotY} r={dotR} /></g>;
  }
  const count = 3 + Math.floor(r[1] * 3);
  const spacing = 26 + r[2] * 28;
  const height = 38 + r[3] * 42;
  const startX = 12 + r[4] * 26;
  const strokeWidth = 4 + r[5] * 4;
  return <g stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round">{Array.from({ length: count }, (_, i) => {
    const x = startX + i * spacing;
    return <path key={i} d={`M${x} 88 L${x + spacing / 2} ${88 - height} L${x + spacing} 88`} />;
  })}</g>;
}

// Keep the original brand asset separate from the artwork so it never varies
// between courses, gets redrawn, or becomes cropped inside an image.
// `decorative` hides the cover from screen readers where the card already shows
// the title as text, so it is not read out twice.
export default function BrandCover({ title, category = 'Learning', resource = false, icon, decorative = false, compact = false, className = '' }) {
  const [ThemeIcon, accent, tint] = themes[category] || [BookOpen, '#087e8b', '#e0f0ef'];
  const Icon = COVER_ICONS[icon] || ThemeIcon;
  const art = buildArt(title || category);
  return (
    <div
      className={`brand-cover${compact ? ' brand-cover--compact' : ''} ${className}`.trim()}
      style={{ '--cover-accent': accent, '--cover-tint': tint }}
      aria-hidden={decorative ? 'true' : undefined}
    >
      <svg className="brand-cover-art" viewBox="0 0 240 100" fill="none" aria-hidden="true" focusable="false">
        {art}
      </svg>
      <div className="brand-cover-header">
        <img className="brand-cover-logo" src="/okiru-logo.png" alt="Okiru" />
        <span className="brand-cover-label">{resource ? 'RESOURCE LIBRARY' : 'OKIRU LEARN'}</span>
      </div>
      <div className="brand-cover-content">
        <span className="brand-cover-category">{category}</span>
        <div className="brand-cover-title">{title}</div>
      </div>
      <div className="brand-cover-footer">
        <span className="brand-cover-rule" />
        <span className="brand-cover-icon"><Icon size={36} strokeWidth={1.4} aria-hidden="true" /></span>
      </div>
    </div>
  );
}
